/* eslint-disable react/jsx-props-no-spreading */

/* eslint-disable jsx-a11y/label-has-associated-control */
import classNames from 'classnames';
import PropTypes from 'prop-types';
import React, { useState, useCallback, useEffect, useMemo } from 'react';
import { FormattedMessage } from 'react-intl';

import useDebounce from '../../hooks/useDebounce';
import { useItemsPaginated } from '../../hooks/useItems';
import useScrollToTop from '../../hooks/useScrollToTop';
import * as AppPropTypes from '../../lib/PropTypes';
import { getComponentFromName } from '../../lib/utils';

import { useApi } from '../../contexts/ApiContext';
import { useUser } from '../../contexts/AuthContext';
import { useEdition } from '../../contexts/EditionContext';
import { useFavoriteItems } from '../../contexts/FavoritesContext';
// import { usePurchaseItems } from '../../contexts/PurchasesContext';
import { useUrlGenerator } from '../../contexts/RoutesContext';
import Card from '../cards/Card';
// import SearchBar from '../forms/SearchBar';
import * as iconComponents from '../icons';
import CalendarIcon from '../icons/Calendar';
import CardsList from '../lists/CardsList';
import LoadingList from '../lists/LoadingList';
import ConfirmEmail from '../notifications/ConfirmEmail';
import EditionsDots from '../partials/EditionsDots';
import IconLink from '../partials/IconLink';
import UserPlaceholder from '../partials/UserPlaceholder';
import UserSidebar from '../partials/UserSidebar';

import styles from '../../../styles/pages/account.module.scss';

const propTypes = {
    page: AppPropTypes.page,
    className: PropTypes.string,
};

const defaultProps = {
    page: null,
    className: null,
};

const AccountPage = ({ className, page }) => {
    const route = useUrlGenerator();
    const api = useApi();

    const defaultEdition = useEdition();
    const [edition, setEdition] = useState(defaultEdition);

    const { accountSection: section, accountSections: sections = [], editions = [] } = page || {};
    const { id = null, description = null, slug = null } = section || {};
    const isFilms = id === 'films';
    const isPurchases = id === 'purchases';
    const isFavorites = id === 'favorites';
    const finalEditions = (editions || [])
        .filter((it) => it !== null)
        .filter(({ handle = null }) => parseInt(handle, 10) > 2020);

    useScrollToTop(section);

    const user = useUser();
    const { emailVerified = false, uniqueHash = null } = user || {};

    // VERY IMPORTANT: Google Calendar is broken with https....
    // Yes it does not make any fucking sense. Leave it to http.
    const uniqueCalendarUrl = useMemo(
        () =>
            uniqueHash !== null
                ? `https://calendar.google.com/calendar/r?cid=http://${
                      window.location.hostname
                  }${route('account.calendar.share', { hash: uniqueHash })}`
                : null,
        [uniqueHash],
    );

    const favorites = useFavoriteItems();
    const purchases = [];
    // const purchases = usePurchaseItems();

    const [searchState, setSearchState] = useState(null);
    const onSearchChange = useCallback(() => {
        if (isFilms) {
            setSearchState(searchState);
        } else {
            setSearchState(null);
        }
    }, [isFilms, setSearchState, searchState]);

    // TODO: clearly this aint working
    useDebounce(onSearchChange, searchState, 300);

    const getFilms = useCallback(
        (newPage, count) =>
            api.account.films({
                search: searchState || null,
                page: newPage,
                count,
            }),
        [api, searchState],
    );

    const {
        items: filmItems,
        allLoaded,
        loadNextPage,
    } = useItemsPaginated(getFilms, {
        pages: null,
        count: 30,
    });

    let currentItems = [];
    switch (id) {
        case 'films':
            currentItems = filmItems || [];
            break;
        case 'calendar':
            currentItems = (favorites || [])
                .filter((it) => it.type === 'event')
                .map((it) => it.item);
            break;
        case 'favorites':
            currentItems = (favorites || [])
                .filter((it) => it.type === 'film')
                .map((it) => it.item);
            break;
        case 'purchases':
            currentItems = (purchases || []).filter((it) => it !== null);
            break;
        default:
            break;
    }

    const finalItems =
        edition !== null && !isPurchases
            ? currentItems.filter((item) => {
                  const { id: editionId } = edition || {};

                  const { edition: itemEdition = null, film = null, type = null } = item || {};
                  const { id: itemEditionId = null } = itemEdition || {};

                  if (type === 'online-film') {
                      const { edition: filmEdition } = film || {};
                      const { id: filmEditionId } = filmEdition || {};
                      return filmEditionId !== null && editionId === filmEditionId;
                  }

                  return itemEditionId !== null && editionId === itemEditionId;
              })
            : currentItems;

    const hasCalendar = id === 'calendar' && finalItems.length > 0;

    const [hasSearch, setHasSearch] = useState(false);
    useEffect(() => {
        if ((filmItems !== null && filmItems.length > 0) || searchState !== null) {
            setHasSearch(true);
        }
    }, [id, filmItems]);

    // Just in case.
    // useEffect(() => {
    //     if (user === null) {
    //         history.push(route('home'));
    //     }
    // }, [user, history, route]);

    return (
        <div
            className={classNames([
                styles.container,
                {
                    [className]: className !== null,
                },
            ])}
        >
            <div className={styles.inner}>
                {user === null ? (
                    <UserPlaceholder className={styles.placeholder} />
                ) : (
                    <>
                        <UserSidebar user={user} className={styles.sidebar} />
                        <div className={styles.main}>
                            {!emailVerified ? (
                                <ConfirmEmail className={styles.notification} />
                            ) : null}
                            <div className={styles.tabs}>
                                <div className={styles.tabHeader}>
                                    {sections.map(
                                        ({
                                            id: sectionId = null,
                                            tab = null,
                                            slug: tabSlug = null,
                                        }) => {
                                            const Icon = getComponentFromName(
                                                iconComponents,
                                                sectionId,
                                            );
                                            return (
                                                <IconLink
                                                    key={`tab-${sectionId}`}
                                                    href={route('account.section', {
                                                        account_section: tabSlug,
                                                    })}
                                                    icon={<Icon />}
                                                    className={classNames(styles.tabButton, {
                                                        [styles.isActiveTab]: tabSlug === slug,
                                                    })}
                                                    iconClassName={styles.tabButtonIcon}
                                                >
                                                    {tab}
                                                </IconLink>
                                            );
                                        },
                                    )}
                                </div>
                                <div className={styles.content}>
                                    <EditionsDots
                                        editions={finalEditions}
                                        edition={edition}
                                        onClick={setEdition}
                                        disabled={isPurchases}
                                    />
                                    {section !== null ? (
                                        <div
                                            key={`tab-${id}`}
                                            className={classNames(styles.tab, {
                                                [styles.isActiveTab]: true,
                                            })}
                                        >
                                            {isFilms ? (
                                                <>
                                                    {/* {hasSearch ? (
                                                <SearchBar
                                                    className={styles.search}
                                                    onChange={setSearchState}
                                                    value={searchState}
                                                />
                                            ) : null} */}
                                                    <div className={styles.terms}>
                                                        {/* <ul> */}
                                                        {/* <li>
                                                        <strong>
                                                            <FormattedMessage
                                                                defaultMessage="Important note: online movie rentals can only be watched from a device within Canada."
                                                                description="Info label"
                                                            />
                                                        </strong>
                                                    </li> */}
                                                        <p>
                                                            <FormattedMessage
                                                                defaultMessage="Online movies are not available this year."
                                                                description="Info label"
                                                            />
                                                        </p>
                                                        {/* </ul> */}
                                                    </div>
                                                    <LoadingList
                                                        withMore={!allLoaded}
                                                        onClickMore={loadNextPage}
                                                    >
                                                        {finalItems.map((item, index) => (
                                                            <Card
                                                                className={styles.card}
                                                                key={`${item?.id}-${index + 1}`}
                                                                item={item}
                                                                {...item}
                                                            />
                                                        ))}
                                                    </LoadingList>
                                                    {/* {hasSearch && finalItems.length === 0 ? (
                                                <p>
                                                    <FormattedMessage
                                                        defaultMessage="No results for this search."
                                                        description="List label"
                                                    />
                                                </p>
                                            ) : null} */}
                                                </>
                                            ) : (
                                                <>
                                                    {hasCalendar ? (
                                                        <div className={styles.buttons}>
                                                            <IconLink
                                                                href={route(
                                                                    'account.calendar.download',
                                                                )}
                                                                icon={<CalendarIcon />}
                                                                className={styles.activeButton}
                                                                external
                                                                target="_self"
                                                            >
                                                                <FormattedMessage
                                                                    defaultMessage="Download calendar (ics)"
                                                                    description="Button label"
                                                                />
                                                            </IconLink>
                                                            {uniqueCalendarUrl !== null ? (
                                                                <IconLink
                                                                    href={uniqueCalendarUrl}
                                                                    icon={<CalendarIcon />}
                                                                    className={styles.activeButton}
                                                                    external
                                                                    target="_blank"
                                                                >
                                                                    <FormattedMessage
                                                                        defaultMessage="Add to Google Calendar"
                                                                        description="Button label"
                                                                    />
                                                                </IconLink>
                                                            ) : null}
                                                        </div>
                                                    ) : null}
                                                    <CardsList
                                                        cardType={isPurchases ? 'purchase' : null}
                                                        items={finalItems}
                                                        cardProps={{
                                                            withFavoriteButton: true,
                                                        }}
                                                        isDark
                                                        isGrid={isFavorites}
                                                        cardClassName={
                                                            isFavorites ? styles.filmGridItem : null
                                                        }
                                                    />
                                                </>
                                            )}
                                            {(!hasSearch || id !== 'films') &&
                                                description !== null &&
                                                description !== '' &&
                                                finalItems.length === 0 && (
                                                    <div className={styles.terms}>
                                                        <p>{description}</p>
                                                    </div>
                                                )}
                                        </div>
                                    ) : null}
                                </div>
                            </div>
                        </div>
                    </>
                )}
            </div>
        </div>
    );
};

AccountPage.propTypes = propTypes;
AccountPage.defaultProps = defaultProps;

export default AccountPage;
